import { io } from "socket.io-client";
import { getClientTimezone } from "../Utils/dateTimeUtils";

let sharedSocket = null;
let sharedSocketUrl = null;
let subscriberCount = 0;
let releaseTimer = null;

const connectionListeners = new Set();

function notifyConnection(status, error = null) {
  for (const listener of connectionListeners) {
    listener(status, error);
  }
}

function createSocket(url) {
  const clientTimezone = getClientTimezone();
  const socket = io(url, {
    transports: ["websocket", "polling"],
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1000,
    auth: {
      clientTimezone,
    },
    transportOptions: {
      polling: {
        extraHeaders: {
          "X-Client-Timezone": clientTimezone,
        },
      },
    },
  });

  socket.on("connect", () => {
    notifyConnection("connected", null);
  });

  socket.on("disconnect", (reason) => {
    const error =
      reason !== "io client disconnect" ? `Disconnected: ${reason}` : null;
    notifyConnection("disconnected", error);
  });

  socket.on("connect_error", (err) => {
    notifyConnection("error", err?.message || "Connection failed");
  });

  return socket;
}

/** Keep one app-wide socket; defer teardown so React Strict Mode remounts do not abort the handshake. */
export function acquireMqttLiveSocket(url) {
  if (!url) return null;

  if (releaseTimer) {
    clearTimeout(releaseTimer);
    releaseTimer = null;
  }

  subscriberCount += 1;

  if (!sharedSocket || sharedSocketUrl !== url) {
    if (sharedSocket) {
      sharedSocket.removeAllListeners();
      sharedSocket.disconnect();
    }
    sharedSocketUrl = url;
    sharedSocket = createSocket(url);
    notifyConnection("connecting", null);
  } else if (sharedSocket.connected) {
    notifyConnection("connected", null);
  } else {
    notifyConnection("connecting", null);
  }

  return sharedSocket;
}

export function releaseMqttLiveSocket() {
  subscriberCount = Math.max(0, subscriberCount - 1);

  releaseTimer = setTimeout(() => {
    releaseTimer = null;
    if (subscriberCount > 0 || !sharedSocket) return;

    sharedSocket.removeAllListeners();
    sharedSocket.disconnect();
    sharedSocket = null;
    sharedSocketUrl = null;
    connectionListeners.clear();
  }, 0);
}

export function subscribeMqttLiveConnection(listener) {
  connectionListeners.add(listener);

  if (sharedSocket?.connected) {
    listener("connected", null);
  } else if (sharedSocket) {
    listener("connecting", null);
  }

  return () => {
    connectionListeners.delete(listener);
  };
}

export function getMqttLiveSocket() {
  return sharedSocket;
}
